<template>
	<div class="cog-picker cog-input" :class="{'is-read-only': readonly }" @click="handleOuterClick">
		<template v-if="readonly">{{ textValue || "&nbsp;" }}</template>
		<c-picker-native v-else-if="chameleon || mobile" :placeholder="placeholder" :show-placeholder="!chameleon && !value">
			<input
				:id="'cog-'+id"
				ref="input"
				v-bind="$attrs"
				:value="nativeTextValue"
				type="date"
				class="el-input__inner"
				:min="$format(min, 'yyyy-MM-dd')"
				:max="$format(max, 'yyyy-MM-dd')"
				@focus="handleFocus"
				@blur="handleBlur"
				@change="handleNativeChange"
			/>
			<template v-if="mobile" #icon>
				<i-calendar :class="{'cog-offscreen': dateTimeIconIsHidden}" />
			</template>
		</c-picker-native>
		<c-date-picker
			v-else
			:id="'cog-'+id"
			ref="picker"
			:value="dateValue"
			:unparsed-value="unparsedValue"
			v-bind="$attrs"
			:format="elementFormat"
			:model-format="modelFormat"
			:placeholder="placeholder"
			:popper-class="'cog-cognito cog-' + scope + ' cog-cognito--' + (chameleon ? 'chameleon' : 'styled') + ' cog-date-picker' + (flags.protectCss ? ' cog-cognito--protect-css' : '')"
			:format-date="(date, format) => $format(date, format)"
			:parse-date="(text, format) => $parse(Date, text, format)"
			:default-value="defaultValue"
			:minimum="normalizedMin"
			:maximum="normalizedMax"
			:picker-options="{firstDayOfWeek: $culture.dateTimeFormat.FirstDayOfWeek}"
			@click="handleClick"
			@focus="handleFocus"
			@blur="handleBlur"
			@input="handleInput"
			@change="handleChange"
			@visible-change="handlePickerVisibleChange"
			@pickerPosition="handlePreFocus"
		>
			<template #suffix>
				<span @click="handleIconClick" @mousedown="handleIconMousedown">
					<i-calendar />
				</span>
			</template>
		</c-date-picker>
	</div>
</template>

<script>
	import 'src/style/_popper.scss';
	import 'src/style/_picker-panel.scss';
	import 'src/style/_input-icon.scss';
	import 'src/style/_scrollbar.scss';
	import 'src/style/_motion.scss';

	import CDatePicker from './DatePicker';
	import CPickerNative from 'src/components/PickerNative.vue';
	import focus from 'src/mixins/focus';
	import ICalendar from 'src/assets/calendar.svg';
	import { convertToElementDateFormat } from 'src/localization/element-format-translation';
	import { normalize } from '@cognitoforms/model.js';
	import { ensureMinHeight } from './form-resizing';
	import { getDeviceType } from '@cognitoforms/utils/user-agent';
	import shouldHideDateTimeIcon from 'src/util/shouldHideDateTimeIcon';

	const DATE_PICKER_HEIGHT = 350;
	export default {
		name: 'CDate',
		components: { ICalendar, CPickerNative, 'c-date-picker': CDatePicker },
		mixins: [focus],
		inject: {
			formEvents: {}, $format: {}, $parse: {}, $expandDateFormat: {}, $culture: {},
			flags: { default: () => ({}) }
		},
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: { type: String, default: null },
			format: { type: String, default: 'd' },
			placeholder: { type: String, default: null },
			readonly: Boolean,
			chameleon: Boolean,
			mobile: Boolean,
			id: { type: String, default: null },
			scope: { type: String, default: '0' },
			min: { type: Date, default: null },
			max: { type: Date, default: null }
		},
		data() {
			return {
				dateValue: null,
				textValue: this.value,
				unparsedValue: this.value,
				nativeTextValue: '',
				isClosing: false,
				isRecentIOS: getDeviceType(window.navigator.userAgent) === 'mac-desktop' && 'ontouchstart' in window,
				dateTimeIconIsHidden: false
			};
		},
		computed: {
			elementFormat: function() {
				return convertToElementDateFormat(this.modelFormat);
			},
			modelFormat: function() {
				return this.$expandDateFormat(this.format);
			},
			normalizedMin: function() {
				return this.min ? normalize(this.min, 'd') : null;
			},
			normalizedMax: function() {
				return this.max ? normalize(this.max, 'd') : null;
			},
			defaultValue: function() {
				if (this.min || this.max) {
					const today = normalize(new Date(), 'd');

					// If the max date is before today (which means that today is not a valid date to select),
					// then use the max date as the default instead of today
					if (today > this.normalizedMax)
						return this.normalizedMax;

					// If the min date is after today (which means that today is not a valid date to select),
					// then use the min date as the default instead of today
					if (today < this.normalizedMin)
						return this.normalizedMin;
				}
				return null;
			}
		},
		watch: {
			value: {
				immediate: true,
				handler(newValue) {
					this.dateValue = newValue ? this.$parse(Date, newValue, this.modelFormat) : null;
					this.textValue = newValue || '';
					this.unparsedValue = this.dateValue ? null : newValue;
				}
			},
			dateValue: {
				immediate: true,
				handler(dateValue) {
					this.nativeTextValue = dateValue ? this.$format(dateValue, 'yyyy-MM-dd') : '';
				}
			}
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.handleResize);
		},
		mounted() {
			// When selecting a date in a table, the date icon can overlap the text (#12315)
			const dateFields = document.querySelectorAll('td.cog-date');
			dateFields.forEach(date => {
				if (date.offsetWidth <= 100) {
					const calIcon = Array.from(date.getElementsByClassName('cog-i-calendar'));
					calIcon.forEach(icon => {
						icon.classList.add('is-hidden');
					});
				}
			});

			const input = this.$el.querySelector('.cog-date--date input[type="text"]');
			if (input && this.isRecentIOS) {
				input.setAttribute('readonly', 'readonly');
				input.setAttribute('aria-readonly', 'false');
			}

			this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			window.addEventListener('resize', this.handleResize);
		},
		methods: {
			handlePreFocus(pickerPosition) {
				if (this.formEvents) {
					const formHeight = document.documentElement.clientHeight;
					const pickerBottomBounds = pickerPosition.bottom + DATE_PICKER_HEIGHT;
					// If there's no room on top nor bottom of the form for thie picker
					if (pickerPosition.top < DATE_PICKER_HEIGHT && pickerBottomBounds > formHeight) {
						// Make the form big enough to accomidate the picker on the bottom
						const minHeight = pickerBottomBounds;
						ensureMinHeight(this.formEvents, minHeight);
					}
				}
			},
			handleOuterClick(evt) {
				// Since the picker won't automatically expand when focussed, then handle open/close when the editor/picker is clicked by mouse
				const picker = this.$refs.picker;
				if (picker) {
					this.handleClick(evt);
				}
			},
			handleFocus(ev) {
				this.addClassOnFocus();
				this.$emit('focus', ev);
				const input = this.$el.querySelector('.cog-date--date input[type="text"]');
				if (input && this.isRecentIOS) {
					input.setAttribute('readonly', 'readonly');
					requestAnimationFrame(()=>{
						input.removeAttribute('readonly');
					});
				}
			},
			handleBlur(ev) {
				// Ignore the blur event if the picker is going to immediately restore focus to the input (ex: hitting Escape or Alt+Up)
				const picker = this.$refs.picker;
				const input = this.$el.querySelector('.cog-date--date input[type="text"]');
				if (picker && picker.willRestoreFocus) {
					if (input && this.isRecentIOS)
						input.removeAttribute('readonly');
					return;
				}

				if (input && this.isRecentIOS)
					input.setAttribute('readonly', 'readonly');

				// Turn off the focused state and raise a blur event
				this.removeClassOnBlur();
				this.$emit('blur', ev);
			},
			handleClick(evt) {
				const picker = this.$refs.picker;
				picker.handleFocus(evt);
				evt.stopPropagation();
			},
			handleIconClick(evt) {
				const picker = this.$refs.picker;
				if (!picker.pickerVisible) {
					picker.$el.querySelector('input').focus();
					picker.handleFocus(evt);
				}
				else {
					picker.softClose();
				}
				evt.stopPropagation();
			},
			handleIconMousedown(evt) {
				// prevent blurring of the date field when clicking the icon
				evt.preventDefault();
			},
			handleInput(value, unparsedValue) {
				if (value && unparsedValue)
					unparsedValue = null;

				if (this.$refs.picker && this.$refs.picker.userInput === null) {
					const textValue = unparsedValue || (typeof value === 'string' ? value : value instanceof Date ? this.$format(value, this.modelFormat) : '');
					const dateValue = unparsedValue ? null : typeof value === 'string' ? this.$parse(Date, value, this.modelFormat) : value || null;
					this.textValue = textValue;
					this.dateValue = dateValue;
					if (unparsedValue) {
						this.$emit('change', unparsedValue);
					}
					else if (textValue) {
						this.$emit('change', textValue);
					}
					else {
						this.$emit('change', null);
					}
				}
			},
			handleChange(value, unparsedValue) {
				const textValue = unparsedValue || (typeof value === 'string' ? value : value instanceof Date ? this.$format(value, this.modelFormat) : '');
				const dateValue = unparsedValue ? null : typeof value === 'string' ? this.$parse(Date, value, this.modelFormat) : value || null;
				this.textValue = textValue;
				this.dateValue = dateValue;
				if (unparsedValue) {
					this.$emit('change', unparsedValue);
				}
				else if (value) {
					this.$emit('change', textValue);
				}
				else {
					this.$emit('change', null);
				}
			},
			handleNativeChange(ev) {
				const dateValue = ev.target.value ? this.$parse(Date, ev.target.value, 'yyyy-MM-dd') : null;
				const textValue = dateValue ? this.$format(dateValue, this.modelFormat) : null;

				this.nativeTextValue = ev.target.value;
				this.textValue = textValue;
				this.dateValue = dateValue;
				this.$emit('change', textValue);
				this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			},
			handlePickerVisibleChange(visible) {
				if (!visible) {
					ensureMinHeight(this.formEvents, 0);
					// When the picker is closed, reset it so that it goes back to its default state/location
					// Use a `setTimeout` so that there isn't a noticeable transition as the picker is closing
					setTimeout(() => {
						if (this.$refs.picker && this.$refs.picker.picker)
							this.$refs.picker.picker.resetDate();
					}, 100);
				}
				else if (this.min && new Date(this.min) > new Date())
					requestAnimationFrame(()=>{
						this.$refs.picker.picker.date = this.min;
					});
			},
			handleResize() {
				this.dateTimeIconIsHidden = shouldHideDateTimeIcon(this.mobile, this.nativeTextValue, this.$refs.input);
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-date .cog-i-calendar {

		g {
			stroke-width: calc(var(--icon-weight) * 1.1px);
		}

		&__rule {
			opacity: .5;
			stroke-width: var(--icon-weight);
		}
	}

	.cog-date-picker {
		max-width: 360px;
		text-align: center;

		.el-picker-panel__content {
			max-width: 340px;
		}

		a {
			text-decoration: none;
		}

		table {
			width: 100%;
			table-layout: fixed;
		}

		.el-date-picker {

			&__editor-wrap {
				display: table-cell;
				position: relative;
				padding: 0 5px;
			}

			&__header {
				display: flex;
				align-items: center;
				justify-content: space-between;
				margin: 12px 15px;

				button {
					position: relative;
					border: 1px solid transparent;
					border-radius: 50%;
					margin-right: 4px;
					margin-left: 4px;
					outline: 0;

					&:focus {
						border-color: var(--highlight);
					}

					&::before,
					&[class*='el-icon-d']::after {
						display: block;
						position: absolute;
						top: 15%;
						width: 60%;
						height: 60%;
						border: calc(var(--icon-weight) * 3px) solid var(--color);
						transform: rotate(45deg) scale(.6);
						content: '';
					}

					&.is-disabled::before,
					&.is-disabled::after {
						opacity: .3;
					}

					&[class*='el-icon-d']::before {
						margin-left: calc(var(--icon-weight) * -1px - 2px);
					}

					&[class*='el-icon-d']::after {
						margin-left: calc(var(--icon-weight) * 1px + 2px);
					}

					&:not(.is-disabled):hover {

						&::before,
						&::after {
							border-color: var(--highlight);
							opacity: 1;
						}
					}
				}

				.el-icon-arrow-left {
					margin-right: auto;
				}

				.el-icon-arrow-right {
					margin-left: auto;
				}

				.el-icon-d-arrow-right {
					order: 100;
				}

				.el-date-picker__prev-btn::before,
				.el-icon-d-arrow-left.el-icon-d-arrow-left::after {
					left: 20%;
					border-top: 0;
					border-right: 0;
				}

				.el-date-picker__next-btn::before,
				.el-icon-d-arrow-right.el-icon-d-arrow-right::after {
					left: 5%;
					border-bottom: 0;
					border-left: 0;
				}

				.el-picker-panel__icon-btn.is-disabled {
					cursor: not-allowed;
					color: hsla(var(--background-hsl), 1);
				}
			}

			&__header-label {
				padding: 0 5px;
				cursor: pointer;
				font-weight: 500;
				line-height: 22px;

				&:hover,
				&.active {
					color: var(--highlight);
				}
			}

			&__prev-btn {
				float: left;
			}

			&__next-btn {
				float: right;
			}
		}

		.el-month-table,
		.el-year-table {
			margin: -1px;
			border-collapse: collapse;

			td {
				padding: 20px 3px;
				cursor: pointer;

				&.is-disabled .el-date-picker__cell {
					cursor: not-allowed;
					/* stylelint-disable-next-line color-function-notation */
					color: hsla(var(--background-hsl));
				}

				.el-date-picker__cell {
					display: block;
					width: 3em;
					margin: 0 auto;
				}
			}
		}

		.el-date-table {

			td {
				position: relative;
				width: 32px;
				height: 30px;
				box-sizing: border-box;
				padding: 4px 0;
				cursor: pointer;
				text-align: center;

				div {
					height: 30px;
					box-sizing: border-box;
					padding: 3px 0;
				}
			}

			th {
				border-bottom: var(--border-width) var(--input__border-style) hsla(var(--background-hsl), .35);
				overflow: hidden;
				padding: 5px;
				font-weight: 400;
				word-wrap: initial;
				overflow-wrap: initial;
				text-overflow: ellipsis;
			}

			.el-date-picker__cell {
				position: absolute;
				left: 50%;
				width: 1.8em;
				height: 1.8em;
				border-radius: 50%;
				margin: 0 auto;
				transform: translateX(-50%);
				line-height: 1.8em;
			}
		}

		.is-current {
			position: relative;

			.el-date-picker__cell {
				color: var(--highlight);
				font-weight: bold;
			}
		}

		.is-selectable:hover {
			color: var(--highlight);
		}

		.is-selected:not(.is-disabled) {

			.el-date-picker__cell {
				display: block;
				background-color: var(--highlight);

				// Give some extra space around numbers, it is especially tight with big font sizes.
				box-shadow: 0 0 0 3px var(--highlight);
				color: var(--highlight-reverse);
			}
		}

		.is-muted,
		.is-disabled {
			color: hsla(var(--background-hsl), .6);
		}

		.is-muted.is-disabled {
			color: hsla(var(--background-hsl), .2);
		}

		.is-disabled {
			background-color: $bgColor;
			cursor: not-allowed;
		}
	}
}
</style>
